export type PublicEvent = {
  id: string;
  title: string;
  description: string | null;
  date: string;
  time: string;
  venue: string;
  location: string;
  locationDetails?: string | null;
  locationPlaceId?: string | null;
  locationPlaceName?: string | null;
  locationFormattedAddress?: string | null;
  locationLat?: number | null;
  locationLng?: number | null;
  showAddress?: boolean;
  heroImage: string | null;
  price: string | null;
  originalPrice: string | null;
  ticketPriceCents?: number | null;
  ticketCurrency?: string | null;
  acceptedPaymentMethods?: string[];
  ticketCategories?: {
    id: string;
    type?: "TICKET" | "COVER_CHARGE";
    label: string;
    price: string | null;
    priceCents: number | null;
    inclusivePriceCents?: number | null;
    currency: string | null;
    originalPrice: string | null;
    originalPriceCents: number | null;
    description: string | null;
    quantityTotal: number | null;
    quantitySold: number | null;
    startsAt: string | null;
    expiresAt: string | null;
    isActive: boolean;
    isSoldOut?: boolean;
    gstRate?: number;
    coverChargeType?: string;
    coverChargeValue?: number;
  }[];
  host: string | null;
  hostId?: string | null;
  hostProfileId?: string | null;
  hostPublicUsername?: string | null;
  hostImageUrl?: string | null;
  hostStats?: {
    hostedCount: number;
    ratingAverage: number;
    ratingCount: number;
  } | null;
  category?: string | null;
  eventType?: string;
  tags?: string[];
  rules?: {
    rule21Plus?: boolean;
    ruleSmartCasual?: boolean;
    ruleCashBar?: boolean;
    ruleNoPlusOnes?: boolean;
    ruleNoPhotos?: boolean;
    ruleShoesOff?: boolean;
    customRules?: string[];
    rulesAndRegulations?: string | null;
  };
  attendeesPreview?: {
    id: string;
    name: string;
    imageUrl: string | null;
  }[];
  attendeesCount?: number;
  status: string;
  isPromoted?: boolean;
  visibility?: "PUBLIC" | "PRIVATE";
  accessControl?: "OPEN" | "INVITE_ONLY";
  requiresHostApproval?: boolean;
  requiredSocialPlatforms?: string[];
  mandatorySocialPlatforms?: string[];
  minRequiredSocials?: number;
  capacity?: number | null;
  maxTicketsPerOrder?: number;
  spotsLeft?: number | null;
  waitlistCount?: number | null;
  restricted?: boolean;
  onlineUrl?: string | null;
  media?: {
    id: string;
    kind: string;
    url: string;
    label: string | null;
  }[];
  performers?: {
    id: string;
    name: string;
    bio: string | null;
    imageUrl: string | null;
    socialLinks: Record<string, string> | null;
    role: string | null;
    performanceStartTime: string | null;
    performanceEndTime: string | null;
    isHeadliner: boolean;
    stageId: string | null;
  }[];
  stages?: {
    id: string;
    name: string;
    description: string | null;
  }[];
};

export type UrgencyIconName =
  | "TRENDING_UP"
  | "ZAP"
  | "STAR"
  | "FLAME"
  | "ALERT_TRIANGLE"
  | "ALERT_CIRCLE";

export type UrgencyTierConfig = {
  id: string;
  minPercent: number;
  maxPercent: number;
  label: string;
  icon: UrgencyIconName;
  accentColor: string;
  badgeBg: string;
  badgeBorder: string;
  barColor: string;
  pulse: boolean;
};

export type UrgencyConfig = {
  version: number;
  tiers: UrgencyTierConfig[];
};

export const URGENCY_ICON_OPTIONS: UrgencyIconName[] = [
  "TRENDING_UP",
  "ZAP",
  "STAR",
  "FLAME",
  "ALERT_TRIANGLE",
  "ALERT_CIRCLE",
];

export const DEFAULT_URGENCY_CONFIG: UrgencyConfig = {
  version: 1,
  tiers: [
    { id: "filling_up", minPercent: 0, maxPercent: 7, label: "Filling Up", icon: "TRENDING_UP", accentColor: "#3B82F6", badgeBg: "rgba(59,130,246,0.10)", badgeBorder: "rgba(59,130,246,0.20)", barColor: "#3B82F6", pulse: false },
    { id: "in_demand", minPercent: 7, maxPercent: 20, label: "In Demand", icon: "ZAP", accentColor: "#8B5CF6", badgeBg: "rgba(139,92,246,0.10)", badgeBorder: "rgba(139,92,246,0.20)", barColor: "#8B5CF6", pulse: false },
    { id: "popular", minPercent: 20, maxPercent: 33, label: "Popular", icon: "STAR", accentColor: "#A855F7", badgeBg: "rgba(168,85,247,0.10)", badgeBorder: "rgba(168,85,247,0.20)", barColor: "#A855F7", pulse: false },
    { id: "selling_fast", minPercent: 33, maxPercent: 50, label: "Selling Fast", icon: "FLAME", accentColor: "#F59E0B", badgeBg: "rgba(245,158,11,0.10)", badgeBorder: "rgba(245,158,11,0.20)", barColor: "#F59E0B", pulse: false },
    { id: "going_quick", minPercent: 50, maxPercent: 67, label: "Going Quick", icon: "FLAME", accentColor: "#F97316", badgeBg: "rgba(249,115,22,0.10)", badgeBorder: "rgba(249,115,22,0.20)", barColor: "#F97316", pulse: false },
    { id: "selling_out", minPercent: 67, maxPercent: 83, label: "Selling Out", icon: "FLAME", accentColor: "#EF4444", badgeBg: "rgba(239,68,68,0.10)", badgeBorder: "rgba(239,68,68,0.20)", barColor: "#EF4444", pulse: false },
    { id: "last_few_spots", minPercent: 83, maxPercent: 90, label: "Last Few Spots", icon: "ALERT_TRIANGLE", accentColor: "#EF4444", badgeBg: "rgba(239,68,68,0.10)", badgeBorder: "rgba(239,68,68,0.20)", barColor: "#EF4444", pulse: true },
    { id: "almost_gone", minPercent: 90, maxPercent: 100, label: "Almost Gone", icon: "ALERT_CIRCLE", accentColor: "#DC2626", badgeBg: "rgba(220,38,38,0.15)", badgeBorder: "rgba(239,68,68,0.30)", barColor: "#DC2626", pulse: true },
  ],
};

export type RsvpStatus =
  | "CONFIRMED"
  | "APPROVED"
  | "PENDING"
  | "WAITLIST"
  | "CANCELLED"
  | "REJECTED"
  | "NONE";

export const INR_SYMBOL = "\u20B9";

export const formatCurrencyText = (value: string | null | undefined) =>
  value ? value.replace(/\bINR\b/g, INR_SYMBOL) : null;

export const parseUrgencyConfig = (value: unknown): UrgencyConfig | null => {
  if (!value || typeof value !== "object") return null;
  const payload = value as { version?: unknown; tiers?: unknown };
  if (typeof payload.version !== "number" || !Array.isArray(payload.tiers) || payload.tiers.length !== 8) {
    return null;
  }
  const tiers: UrgencyTierConfig[] = [];
  for (const item of payload.tiers) {
    if (!item || typeof item !== "object") return null;
    const tier = item as Record<string, unknown>;
    const icon = tier.icon;
    if (typeof icon !== "string" || !URGENCY_ICON_OPTIONS.includes(icon as UrgencyIconName)) {
      return null;
    }
    if (
      typeof tier.id !== "string" ||
      typeof tier.label !== "string" ||
      typeof tier.minPercent !== "number" ||
      typeof tier.maxPercent !== "number" ||
      typeof tier.accentColor !== "string" ||
      typeof tier.badgeBg !== "string" ||
      typeof tier.badgeBorder !== "string" ||
      typeof tier.barColor !== "string" ||
      typeof tier.pulse !== "boolean"
    ) {
      return null;
    }
    tiers.push({
      id: tier.id,
      minPercent: tier.minPercent,
      maxPercent: tier.maxPercent,
      label: tier.label,
      icon: icon as UrgencyIconName,
      accentColor: tier.accentColor,
      badgeBg: tier.badgeBg,
      badgeBorder: tier.badgeBorder,
      barColor: tier.barColor,
      pulse: tier.pulse,
    });
  }
  return { version: payload.version, tiers: tiers.sort((a, b) => a.minPercent - b.minPercent) };
};

export type SuperGuestSummary = {
  isSuperGuest: boolean;
  name?: string;
  maxInvites?: number;
  invitesSent?: number;
  invitesLeft?: number;
};
